import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdminTenant } from '@/features/tenants/useTenants';
import { useUpdateStatus } from './usePlans';

type Status = 'active' | 'past_due' | 'suspended';

/**
 * Change a tenant's account status — the dunning ladder's ONE real rung
 * (slice 20, canvas `5f`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 SUSPENDING LOCKS A PAYING CUSTOMER OUT OF THEIR OWN FLEET.
 * ─────────────────────────────────────────────────────────────────────────────
 * `tenancy/middleware.ts` refuses every request from a suspended tenant with
 * `tenant_suspended`. This is the most consequential button in the admin portal
 * after impersonation, so it asks for a typed confirmation of the tenant's name
 * rather than a single click: a mis-click on the wrong row of a list is exactly
 * the failure mode, and the name is the thing that distinguishes the rows.
 *
 * WHAT THE COPY MUST SAY, AND WHY IT IS NOT DECORATION. Suspension hides the
 * VIEW. Positions keep arriving, trips keep being detected, and nothing is
 * deleted — "never delete client history" is the specific weakness this product
 * sells against, and a staff member who believes suspending destroys data will
 * hesitate at the moment the ladder exists for. It is also stated that no engine
 * is ever cut for a bill, which is a standing rule and now drawn on the canvas.
 */
export function StatusDialog({
  tenant,
  onClose,
}: {
  tenant: AdminTenant;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>(tenant.status);
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(false);
  const update = useUpdateStatus(tenant.id);

  // Only the destructive direction needs the name typed. Restoring an account
  // is the recovery from a mistake and must not be harder than making one.
  const needsConfirm = status === 'suspended' && tenant.status !== 'suspended';
  const ready =
    status !== tenant.status &&
    (!needsConfirm || confirm.trim().toLowerCase() === tenant.name.trim().toLowerCase());

  async function submit() {
    if (!ready) return;
    setError(false);
    try {
      await update.mutateAsync({ status });
      onClose();
    } catch {
      setError(true);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('plans.status.title', { name: tenant.name })}
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/60 p-0 sm:items-start sm:p-8"
    >
      <div className="w-full min-h-dvh rounded-none border-0 bg-raad-panel p-5 pt-[calc(1.25rem+var(--raad-safe-top))] sm:min-h-0 sm:max-w-md sm:rounded-2xl sm:border sm:border-raad-border sm:p-6 sm:pt-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-[17px] font-semibold">
            {t('plans.status.title', { name: tenant.name })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold"
          >
            {t('plans.close')}
          </button>
        </div>

        <label className="block">
          <span className="mb-1 block text-[12px] font-medium text-raad-muted">
            {t('plans.status.label')}
          </span>
          <select
            className="w-full min-h-10 rounded-lg border border-raad-border bg-transparent px-3 text-[13.5px] outline-none focus:border-raad-primary"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as Status);
              setConfirm('');
            }}
          >
            <option value="active">{t('plans.statusName.active')}</option>
            <option value="past_due">{t('plans.statusName.past_due')}</option>
            <option value="suspended">{t('plans.statusName.suspended')}</option>
          </select>
        </label>

        <p className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[12.5px]">
          {t('plans.status.whatItDoes')}
        </p>

        {needsConfirm ? (
          <label className="mt-3 block">
            <span className="mb-1 block text-[12px] font-medium text-raad-muted">
              {t('plans.status.typeName', { name: tenant.name })}
            </span>
            <input
              className="w-full min-h-10 rounded-lg border border-raad-border bg-transparent px-3 text-[13.5px] outline-none focus:border-raad-primary"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </label>
        ) : null}

        {error ? (
          <p role="alert" className="mt-3 text-[12.5px] text-raad-error">
            {t('plans.status.failed')}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!ready || update.isPending}
            className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white disabled:opacity-50"
          >
            {update.isPending ? t('plans.status.saving') : t('plans.status.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
